import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, MessageSquareText, FileCheck2, FileText, Building2, MessageCircle } from "lucide-react";
import { Reveal, SectionTitle } from "./Bits";

const STEPS = [
  { icon: Palette, n: "01", title: "Personalize uma vez", desc: "Logo, cores, fonte e modelo A4. Cada empresa guarda o próprio estilo." },
  { icon: MessageSquareText, n: "02", title: "Peça no WhatsApp", desc: "Descreva o serviço em uma mensagem, do seu jeito. A IA entende e pergunta só o que faltar." },
  { icon: FileCheck2, n: "03", title: "Receba o PDF", desc: "O orçamento chega pronto na conversa, com a sua marca. Peça ajustes e ele reenvia." },
];

const SCRIPT = [
  { from: "me", text: "Orçamento de sonorização do casamento da Ana Lima, dia 20/10, por R$ 2.500. Pode gerar." },
  { from: "bot", text: "Orçamento pronto. 📄", pdf: true },
  { from: "me", text: "usa a Mega Eventos" },
  { from: "bot", text: "✅ Agora estamos usando *Mega Eventos*. Me diga o que você quer orçar." },
  { from: "me", text: "Painel de LED 4x3 pro Marcelo, R$ 1.800" },
  { from: "bot", text: "Orçamento pronto. 📄", pdf: true },
];

const Bubble = ({ m }) => (
  <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.35 }}
    className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-snug shadow-sm ${m.from === "me" ? "self-end bg-[#d9fdd3] text-slate-800 rounded-br-md" : "self-start bg-white text-slate-800 rounded-bl-md"}`}>
    {m.pdf ? (
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center w-9 h-9 rounded-lg bg-rose-50 text-rose-600"><FileText size={16} /></span>
        <div>
          <div className="font-semibold text-[12.5px]">Proposta.pdf</div>
          <div className="text-[11px] text-slate-500">{m.text}</div>
        </div>
      </div>
    ) : (
      m.text.split("*").map((t, i) => (i % 2 ? <b key={i}>{t}</b> : <span key={i}>{t}</span>))
    )}
  </motion.div>
);

const WhatsAppMock = () => {
  const [n, setN] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setN((k) => (k >= SCRIPT.length ? 1 : k + 1)), 1700);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative mx-auto w-full max-w-[340px] rounded-[28px] bg-slate-900 p-2.5 shadow-[0_40px_80px_-30px_rgba(15,23,42,0.5)]" data-testid="upgrade-whatsapp-demo">
      <div className="rounded-[22px] overflow-hidden bg-[#efe7dd]">
        <div className="flex items-center gap-2.5 bg-[#075E54] px-4 py-3 text-white">
          <span className="grid place-items-center w-8 h-8 rounded-full bg-white/15"><MessageCircle size={16} /></span>
          <div className="leading-tight">
            <div className="text-[13px] font-semibold">Basiq</div>
            <div className="text-[10.5px] text-white/70">assistente de orçamentos</div>
          </div>
        </div>
        <div className="flex flex-col gap-2 p-3 min-h-[360px]">
          <AnimatePresence initial={false}>
            {SCRIPT.slice(0, n).map((m, i) => <Bubble key={i} m={m} />)}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default function HowItWorksWA() {
  return (
    <section className="mt-24" data-testid="upgrade-how">
      <Reveal>
        <SectionTitle eyebrow="Como funciona" title="Orçamentos pelo WhatsApp, em segundos." sub="Nos planos Standard e Pro você conecta seu WhatsApp uma vez e passa a orçar direto da conversa, inclusive alternando entre suas empresas por mensagem." />
      </Reveal>
      <div className="mt-10 grid lg:grid-cols-2 gap-10 items-center">
        <Reveal>
          <div className="space-y-4">
            {STEPS.map((s) => (
              <div key={s.n} className="flex gap-4 rounded-2xl bg-white ring-1 ring-slate-200 p-5">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#EEF0FF] text-[#4A3BFA] shrink-0"><s.icon size={19} /></span>
                <div>
                  <div className="text-[11px] font-mono-jb text-slate-400">{s.n}</div>
                  <div className="text-[15px] font-semibold text-slate-900">{s.title}</div>
                  <p className="text-[13.5px] text-slate-500 mt-1 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
            <div className="flex items-start gap-3 rounded-2xl bg-[#25D366]/10 ring-1 ring-[#25D366]/25 p-4">
              <Building2 size={18} className="text-[#128C7E] shrink-0 mt-0.5" />
              <p className="text-[13px] text-slate-700"><b>Várias empresas, um só WhatsApp.</b> Diga "usa a Mega" e o próximo orçamento sai com a logo, cores e modelo dessa empresa.</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}><WhatsAppMock /></Reveal>
      </div>
    </section>
  );
}
